import { ApolloQueryResult } from '@apollo/client';
import { useAtomValue } from 'jotai';

import { LDFlags } from '../../../../../constants/flags';
import {
  ExternalUserPayoffRequestQuery,
  PayoffRequestStatus,
  useExternalUserPayoffRequestQuery,
  useOnPayoffRequestStatusUpdateSubscription,
} from '../../../../../gql/generated/graphql';
import useFlag from '../../../../../hooks/useFlag';
import useMinDwell from '../../../../../hooks/useMinDwell';
import { DealWithFarthestState } from '../../../utils';
import {
  devAfterHoursHeldAtom,
  devIs5StepAtom,
  devPayoffStatusAtom,
  devSelfServeSubmittedAtom,
  isDevActiveAtom,
} from '../../devToolbarAtoms';
import {
  PayoffSource,
  PayoffStepStatus,
  getPayoffStepStatus,
  getStepperActiveStep,
} from '../utils';

const getPayoffSource = (selfServeSubmittedAt?: string | Date | null): PayoffSource | undefined => {
  if (selfServeSubmittedAt) return 'self-serve';
  return undefined;
};

const MIN_PENDING_DWELL_MS = 5000;

const useStepperState = (
  deal: DealWithFarthestState,
  isOpen: boolean,
): {
  activeStep: number;
  payoffStepStatus: PayoffStepStatus | undefined;
  payoffSource: PayoffSource | undefined;
  is5StepVariant: boolean;
  payoffLoading: boolean;
  refetchPayoff: () => Promise<ApolloQueryResult<ExternalUserPayoffRequestQuery>>;
} => {
  const {
    data: payoffData,
    loading: payoffLoading,
    refetch: refetchPayoff,
  } = useExternalUserPayoffRequestQuery({
    variables: { dealId: deal?.id },
    skip: !deal?.id,
  });

  const isDevActive = useAtomValue(isDevActiveAtom);
  const devIs5Step = useAtomValue(devIs5StepAtom);
  const devAfterHoursHeld = useAtomValue(devAfterHoursHeldAtom);
  const devPayoffStatus = useAtomValue(devPayoffStatusAtom);
  const devSelfServeSubmitted = useAtomValue(devSelfServeSubmittedAtom);

  const afterHoursSelfServiceEnabled = useFlag(LDFlags.AFTER_HOURS_SELF_SERVICE_PAYOFF);

  const piCompleted = isDevActive
    ? devIs5Step && !devAfterHoursHeld
    : payoffData?.externalUserPayoffRequest?.is_payoff_intelligence === true &&
      payoffData?.externalUserPayoffRequest?.status === PayoffRequestStatus.Completed;

  const isAfterHoursHeld = isDevActive ? devAfterHoursHeld : !!deal?.after_hours_held_at && !isOpen;

  const payoffStatus = isDevActive
    ? devPayoffStatus ?? payoffData?.externalUserPayoffRequest?.status
    : payoffData?.externalUserPayoffRequest?.status;

  const realIs5StepVariant = afterHoursSelfServiceEnabled
    ? piCompleted || isAfterHoursHeld
    : piCompleted;

  const is5StepVariant = isDevActive ? devIs5Step || devAfterHoursHeld : realIs5StepVariant;

  const liveStatus = getPayoffStepStatus({
    is5StepVariant,
    piCompleted,
    isAfterHoursHeld,
    payoffStatus,
  });

  const payoffStepStatus = useMinDwell(liveStatus, 'pending', MIN_PENDING_DWELL_MS);

  const payoffSource = isDevActive
    ? getPayoffSource(devSelfServeSubmitted ? new Date() : null)
    : getPayoffSource(payoffData?.externalUserPayoffRequest?.self_serve_submitted_at);

  const activeStep = getStepperActiveStep({ deal, is5StepVariant, payoffStepStatus });

  useOnPayoffRequestStatusUpdateSubscription({
    variables: { dealId: deal?.id },
    skip: !deal?.id || !is5StepVariant,
    onData: () => {
      refetchPayoff();
    },
  });

  return {
    activeStep,
    payoffStepStatus,
    payoffSource,
    is5StepVariant,
    payoffLoading,
    refetchPayoff,
  };
};

export default useStepperState;
